import React from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Typography,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ILabel } from '../../constants/interfaces';
import { formUi } from '../../constants/globalcss';
import {
  getFieldRenderer,
  resolveFieldType,
} from '../../components/fieldRenderers';

interface LabelComponentProps {
  label: ILabel;
  value: string;
  onChange: (labelId: number, value: string) => void;
  error?: string;
}

interface FormLabelProps {
  text: string;
  isMandatory?: boolean;
  error?: string;
}

const FormLabel: React.FC<FormLabelProps> = ({ text, isMandatory, error }) => {
  const theme = useTheme();

  return (
    <Typography
      variant="body2"
      sx={{
        color: error ? theme.palette.error.main : theme.palette.text.primary,
        flexShrink: 0,
        fontWeight: theme.typography.fontWeightMedium,
        overflowWrap: 'break-word',
        mb: 0.5,
        textAlign: 'left',
        width: '100%',
        wordBreak: 'break-word',
      }}
    >
      {text}
      {isMandatory && (
        <span style={{ color: theme.palette.error.main }}> *</span>
      )}
    </Typography>
  );
};

export const LabelComponent: React.FC<LabelComponentProps> = ({
  label,
  value,
  onChange,
  error,
}) => {
  const theme = useTheme();

  const handleChange = (newValue: string) => {
    onChange(label.labelId, newValue);
  };

  // Common styles
  const fieldContainerStyles = {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
    minWidth: 0,
    width: '100%',
  };
  const fieldWidth = '100%';
  const textFieldStyles = {
    '& .MuiFormHelperText-root': {
      mt: formUi.field.helperSpacingTop,
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: formUi.field.borderFocus,
        borderWidth: 2,
      },
      '&:hover fieldset': {
        borderColor: formUi.field.borderHover,
      },
      minHeight: formUi.field.inputHeight,
      backgroundColor: formUi.card.background,
      borderRadius: formUi.card.radius,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: formUi.field.borderDefault,
    },
    boxSizing: 'border-box',
    maxWidth: '100%',
    minWidth: 0,
    width: fieldWidth,
  };
  const menuItemStyles = {
    minHeight: 'auto',
    px: 2,
    py: 1.5,
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  };
  const typographyStyles = {
    lineHeight: 1.4,
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  };

  /**
   * Field rendering is shared with the request form: the same configured label type
   * produces the same control in both places, rather than each form reimplementing
   * the types with its own MUI markup.
   *
   * The local cases below remain for the types the shared registry does not cover
   * yet — radio and checkbox — and as the fallback for an unrecognised type.
   */
  const renderFromSharedRegistry = () => {
    const renderer = getFieldRenderer(label.type);
    if (!renderer) {
      return null;
    }
    return renderer.render({
      commonProps: {
        displayOnly: false,
        errorText: error,
        field: label,
        isLabelHidden: false,
        onChange: handleChange,
        value,
        width: fieldWidth,
      },
      field: label,
      onAuxiliaryFieldChange: () => undefined,
      onChange: handleChange,
    });
  };

  const renderByType = () => {
    const shared = renderFromSharedRegistry();
    if (shared) {
      return <Box sx={fieldContainerStyles}>{shared}</Box>;
    }

    switch (resolveFieldType(label.type)) {
      // Only the types the shared registry does not cover reach this point.
      case 'radio':
        return (
          <Box sx={{ ...fieldContainerStyles, width: '100%' }}>
            <Typography
              variant="body2"
              sx={{
                color: error ? 'error.main' : 'text.primary',
                fontWeight: 500,
                mb: 1,
              }}
            >
              {label.text}
              {label.isMandatory && (
                <Chip
                  label="Required"
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ height: 20, ml: 1 }}
                />
              )}
            </Typography>
            <RadioGroup
              value={value}
              onChange={(e) => handleChange(e.target.value)}
            >
              {label.options?.map((option) => (
                <FormControlLabel
                  key={option.labelOptionId}
                  value={option.text}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: theme.palette.primary.main },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={typographyStyles}>
                      {option.text}
                    </Typography>
                  }
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />
              ))}
            </RadioGroup>
            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
          </Box>
        );

      case 'checkbox':
        return (
          <Box sx={{ width: '100%' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={value === 'true' || value === '1'}
                  onChange={(e) =>
                    handleChange(e.target.checked ? 'true' : 'false')
                  }
                  sx={{
                    '&.Mui-checked': { color: theme.palette.primary.main },
                  }}
                />
              }
              label={
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                  {label.text}
                  {label.isMandatory && (
                    <Chip
                      label="Required"
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ height: 20 }}
                    />
                  )}
                </Box>
              }
            />
          </Box>
        );

      default:
        return (
          <Box sx={fieldContainerStyles}>
            <FormLabel
              text={label.text}
              isMandatory={label.isMandatory}
              error={error}
            />
            <TextField
              fullWidth
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              error={!!error}
              helperText={error || `Type: ${label.type}`}
              sx={textFieldStyles}
            />
          </Box>
        );
    }
  };

  return <Box sx={{ minWidth: 0, width: '100%' }}>{renderByType()}</Box>;
};
